import { handleGetData } from '../../Api';
import { semAcento } from '../PhotoGallery/dados';

export { aplicarMetadados, anoDe, contagem, dataPorExtenso, semAcento } from '../PhotoGallery/dados';

// A lista e a matéria leem a página (`pagina-noticias`, os textos) e um índice
// leve de todas as matérias publicadas: só título, data, resumo, capa e
// endereço (o X-Fields deixa o texto de fora). Com ele a busca sem acento, o
// filtro por ano, a contagem e "notícia anterior/próxima" funcionam no
// navegador -- o Squidex não busca pedaço de palavra sem acento. Fica guardado
// enquanto o visitante anda entre a lista e as matérias.
const CAMPOS_DO_INDICE = 'titulo,data,resumo,capa,endereco';
let emAndamento = null;

// O Squidex devolve no máximo 200 por página, mesmo pedindo mais. O primeiro
// pedido diz o total; as outras páginas vão todas juntas (uma depois da outra,
// as 8 páginas de produção levavam ~9 s no celular).
const POR_PAGINA = 200;
const pagina = (token, pular) =>
  handleGetData(
    token,
    `api/content/copasul/materia/?$top=${POR_PAGINA}&$skip=${pular}&$orderby=data/data/iv desc`,
    { 'X-Fields': CAMPOS_DO_INDICE },
  );

const buscarIndice = async (token) => {
  const primeira = await pagina(token, 0);
  const itens = primeira?.data?.items || [];
  const total = primeira?.data?.total ?? itens.length;
  if (!itens.length || itens.length >= total) return itens;
  const passo = itens.length; // o que o Squidex de fato devolve por página
  const pulos = [];
  for (let pular = passo; pular < total; pular += passo) pulos.push(pular);
  const resto = await Promise.all(pulos.map((pular) => pagina(token, pular)));
  return [...itens, ...resto.flatMap((r) => r?.data?.items || [])];
};

const paraNoticia = (item) => {
  const d = item.data || {};
  return {
    id: item.id,
    titulo: d.titulo?.iv || '',
    data: d.data?.iv || '',
    resumo: d.resumo?.iv || '',
    capa: d.capa?.iv?.[0] || null,
    endereco: d.endereco?.iv || '',
  };
};

export const carregarNoticias = (token) => {
  if (!emAndamento) {
    emAndamento = Promise.all([
      handleGetData(token, 'api/content/copasul/pagina-noticias/').then(
        (r) => r?.data?.items?.[0]?.data || {},
      ),
      buscarIndice(token),
    ])
      .then(([pagina, itens]) => {
        const campo = (nome) => pagina[nome]?.iv || {};
        const lista = itens.map(paraNoticia).filter((n) => n.endereco);
        return {
          topo: campo('topo'),
          emFoco: campo('emFoco'),
          faixaFinal: pagina.faixaFinal?.iv || null,
          metadados: campo('metadados'),
          lista,
        };
      })
      .catch((e) => {
        emAndamento = null;
        throw e;
      });
  }
  return emAndamento;
};

// A matéria inteira (com o texto), pelo endereço. Guardada: voltar para uma já
// lida não busca de novo.
const materias = new Map();

const paraMateria = (item) => {
  const d = item.data || {};
  return {
    ...paraNoticia(item),
    legendaCapa: d.legendaCapa?.iv || '',
    capaNaMateria: d.capaNaMateria?.iv !== false,
    corpo: d.corpo?.iv || [],
    metadados: d.metadados?.iv || null,
  };
};

const buscarUma = async (token, filtro) => {
  const r = await handleGetData(
    token,
    `api/content/copasul/materia/?$top=1&$filter=${encodeURIComponent(filtro)}`,
  );
  const item = r?.data?.items?.[0];
  return item ? paraMateria(item) : null;
};

const aspas = (texto) => `'${String(texto).replace(/'/g, "''")}'`;

export const carregarMateria = (token, endereco) => {
  if (!materias.has(endereco)) {
    materias.set(
      endereco,
      buscarUma(token, `data/endereco/iv eq ${aspas(endereco)}`).catch((e) => {
        materias.delete(endereco);
        throw e;
      }),
    );
  }
  return materias.get(endereco);
};

// Endereço do modelo antigo: /paginaNoticia/<idUrl>. A conversão guardou esse
// idUrl em `enderecoAntigo`, então a notícia é achada por ele.
export const enderecoDaAntiga = async (token, antigo) => {
  const materia = await buscarUma(token, `data/enderecoAntigo/iv eq ${aspas(antigo)}`);
  return materia?.endereco || null;
};

// "08", "outubro", "2026" -- a data em UTC, como a data pura que ela é (ver
// dataPorExtenso).
const partes = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' });
export const partesDaData = (iso) => {
  if (!iso) return { dia: '', mes: '', ano: '' };
  const p = Object.fromEntries(partes.formatToParts(new Date(iso)).map(({ type, value }) => [type, value]));
  return { dia: p.day, mes: p.month, ano: p.year };
};

// Texto puro de um trecho de HTML do Squidex.
export const textoDe = (html = '') =>
  html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

// Minutos de leitura, a ~200 palavras por minuto.
export const minutosDeLeitura = (corpo = []) => {
  const palavras = corpo
    .map((b) => textoDe(b.texto || ''))
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(palavras / 200));
};

// O resumo some na matéria aberta quando é o próprio começo do texto (a
// conversão das notícias antigas usou a 1ª frase como resumo).
export const resumoRepeteOTexto = (resumo, corpo = []) => {
  if (!resumo) return true;
  const comeco = semAcento(textoDe(corpo.find((b) => b.texto)?.texto || '')).replace(/[…."]+$/, '');
  const r = semAcento(resumo).replace(/[…."]+$/, '');
  return comeco.startsWith(r.slice(0, 80));
};

// A notícia em foco: a escolhida no Squidex ou, sem escolha (ou se ela saiu do
// ar), a mais recente.
export const emFocoDe = (noticias) => {
  const escolhida = noticias.emFoco?.materia?.[0];
  return noticias.lista.find((n) => n.id === escolhida) || noticias.lista[0] || null;
};

// Rótulos de interface: sempre os mesmos, ficam aqui e não no Squidex.
export const ROTULOS = {
  emFoco: 'Em foco',
  lerNoticia: 'Ler notícia',
  filtrosChapeu: 'Encontre uma notícia',
  filtrosTitulo: 'Busca e filtros',
  palavraChave: 'Palavra-chave',
  busca: 'O que você procura?',
  ano: 'Ano de publicação',
  todos: 'Todos',
  outrosAnos: 'Outros anos',
  selecioneAno: 'Selecione um ano',
  limpar: 'Limpar filtros',
  publicacoes: 'Publicações',
  noticia: ['notícia', 'notícias'],
  maisPublicacoes: 'Mais publicações',
  vazio: 'Nenhuma notícia nesta seleção.',
  fim: 'Você chegou ao fim da seleção',
  publicadoEm: 'Publicado em',
  minLeitura: 'min de leitura',
  compartilhar: 'Compartilhar',
  copiado: 'Link da notícia copiado.',
  copieOEndereco: 'Copie o endereço desta página para compartilhar.',
  emImagem: 'Em imagem',
  ampliar: 'Ampliar foto',
  anterior: 'Notícia anterior',
  proxima: 'Próxima notícia',
  maisAntiga: 'Você chegou à notícia mais antiga.',
  maisRecente: 'Você está na notícia mais recente.',
  ajuda: '← → Navegar · Esc Fechar · + Ampliar',
};
